import React, { useRef, useState } from 'react';

import { Box, Button, Flex, ScrollArea, Space, TextInput, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Content as EditorFileContent } from '@tiptap/react';
import { EditorRef, SmartEditor } from 'pages/SmartEditor/SmartEditor';
import { useTranslation } from 'react-i18next';
import { EMPTY_SMART_DOCUMENT } from 'shared/constants/empty-new-smart-document';
import { createSmartEditorFile } from 'shared/utils/create-smart-editor-file';
import { maxLengthValidator, minLengthValidator, requiredInputValidator } from 'shared/utils/form-validators';

interface RawTextSelectorProps {
  onTextResourceAdd: (file: File) => void;
}

export function RawTextSelector({ onTextResourceAdd }: RawTextSelectorProps) {
  const [fileContent, setFileContent] = useState<EditorFileContent>(EMPTY_SMART_DOCUMENT);
  const [isEmptyFile, setIsEmptyFile] = useState(true);
  const editorRef = useRef<EditorRef>(null);
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const fileNameForm = useForm({
    initialValues: { fileName: '' },
    validate: {
      fileName: (value) => requiredInputValidator(value) || minLengthValidator(value) || maxLengthValidator(value)
    }
  });

  const addTextResource = () => {
    const validation = fileNameForm.validate();
    if (validation.hasErrors) return;
    const textFile = createSmartEditorFile(fileNameForm.values.fileName, fileContent);
    onTextResourceAdd(textFile);
    fileNameForm.reset();
    setFileContent(EMPTY_SMART_DOCUMENT);
    setIsEmptyFile(true);
    editorRef.current?.clearEditorContent();
  };

  const onTextEditorChange = () => {
    const content = editorRef.current?.exportEditorContent();
    if (!content) {
      setIsEmptyFile(true);
      return;
    }
    const stringContent = JSON.stringify(content);
    if (stringContent === JSON.stringify(EMPTY_SMART_DOCUMENT)) {
      setIsEmptyFile(true);
      return;
    }
    setFileContent(content);
    setIsEmptyFile(false);
  };

  return (
    <Flex w="100%" h="100%" px={20} pt={70} pb={4} direction="column" align="center" justify="center">
      <TextInput
        w="100%"
        placeholder={t('BATCH_RESOURCE_SELECTOR.RAW_TEXT.FILE_NAME')}
        {...fileNameForm.getInputProps('fileName')}></TextInput>
      <Space h={20} />
      <ScrollArea w="100%" h="80%" p={20} style={{ border: `1px solid ${theme.colors.gray[3]}`, borderRadius: 7 }}>
        <SmartEditor onChange={onTextEditorChange} fileContent={EMPTY_SMART_DOCUMENT} ref={editorRef} />
      </ScrollArea>
      <Space h={30} />
      <Box w="100%">
        <Button disabled={isEmptyFile} style={{ float: 'right' }} onClick={() => addTextResource()}>
          {t('BATCH_RESOURCE_SELECTOR.RAW_TEXT.ADD')}
        </Button>
      </Box>
    </Flex>
  );
}
